import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useCan } from "@/lib/authz";
import { useAuth } from "@/lib/auth";
import { useRoleName, RoleName } from "@/lib/roles";
import {
  UserPlus, PencilSimple, Key, ArrowsLeftRight, Power, MagnifyingGlass, ClockCounterClockwise,
  UploadSimple, User as UserIcon,
} from "@phosphor-icons/react";

/** Phase 6C — Full Employee Master UI.
 *  Exposes every field implemented by the backend:
 *    - employee_code (read-only, auto-generated)
 *    - name / first_name / last_name / email / mobile
 *    - date_of_joining, employment_status
 *    - department, designation, office_location, company (master dropdowns)
 *    - role (primary) + roles[] (secondary, multi)
 *    - reporting_manager_id, team
 *    - max_capacity (per-employee, default 30)
 *    - profile_picture (upload after create)
 *  Uses:
 *    - POST /api/employees/full        (create with new schema)
 *    - PATCH /api/employees/{id}/master (update with optimistic concurrency)
 *    - DELETE /api/employees/{id}/soft  (soft delete)
 *    - POST /api/employees/{id}/profile-picture
 *    - GET /api/directory/employees     (paginated, filtered listing)
 *  Legacy transfer + reset-password endpoints preserved.
 */

const EMPTY_CREATE = {
  name: "", first_name: "", last_name: "", email: "", password: "",
  mobile: "", date_of_joining: "",
  employment_status: "Active",
  department: "", designation: "", office_location: "", company: "",
  role: "sde", roles: [],
  reporting_manager_id: "", team: "",
  managed_teams: [], managed_groups: [],
  max_capacity: 30,
};

function StatusBadge({ status }) {
  const map = {
    Active: "bg-emerald-100 text-emerald-700",
    Inactive: "bg-slate-200 text-slate-600",
    "On Leave": "bg-amber-100 text-amber-700",
    Resigned: "bg-red-100 text-red-700",
  };
  return <span data-testid={`emp-status-${(status || "").toLowerCase().replace(" ", "-")}`}
    className={`text-xs px-2 py-0.5 rounded-full ${map[status] || map.Inactive}`}>{status || "Inactive"}</span>;
}

export default function Employees() {
  const { user, refresh: refreshMe } = useAuth();
  const roleName = useRoleName();
  const isAdmin = user?.role === "admin";
  // Phase A/B/C — permission-based gates. Only render Edit/Delete/Create if role has the perm.
  const canEditEmployee = useCan("employee.edit");
  const canDeleteEmployee = useCan("employee.delete");
  const canCreateEmployee = useCan("employee.create");
  const canManage = canEditEmployee || canDeleteEmployee || canCreateEmployee;
  const [groupsCatalog, setGroupsCatalog] = useState([]);
  useEffect(() => { (async () => { try { const r = await api.get("/masters/groups"); setGroupsCatalog(r.data.rows || []); } catch {} })(); }, []);

  // UAT Batch 1 — role-scoped valid-reporting-managers cache. Fetched on
  // demand as the Create/Edit modal role selector changes.
  const [createValidManagers, setCreateValidManagers] = useState([]);
  const [editValidManagers, setEditValidManagers] = useState([]);

  // Data
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [teamFilter, setTeamFilter] = useState("");

  // Masters
  const [rolesCatalog, setRolesCatalog] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [employmentStatuses, setEmploymentStatuses] = useState([]);
  const [officeLocations, setOfficeLocations] = useState([]);
  const [companies, setCompanies] = useState([]);

  // UI state
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [transferring, setTransferring] = useState(null);
  const [uploadingPic, setUploadingPic] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [transfers, setTransfers] = useState([]);

  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [editForm, setEditForm] = useState({});
  const [newPassword, setNewPassword] = useState("");
  const [transferTo, setTransferTo] = useState("");
  const [transferReason, setTransferReason] = useState("");

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page, page_size: pageSize });
    if (search.trim()) params.set("q", search.trim());
    if (roleFilter) params.set("role", roleFilter);
    if (statusFilter) params.set("employment_status", statusFilter);
    if (teamFilter) params.set("team", teamFilter);
    try {
      const r = await api.get(`/directory/employees?${params.toString()}`);
      setItems(r.data.rows || []);
      setTotal(r.data.total || 0);
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  }, [page, pageSize, search, roleFilter, statusFilter, teamFilter]);

  const loadMasters = useCallback(async () => {
    try {
      const [rls, dept, des, es, ol, co] = await Promise.all([
        api.get("/masters/roles"),
        api.get("/masters/departments?page_size=200"),
        api.get("/masters/designations?page_size=200"),
        api.get("/masters/employment-statuses"),
        api.get("/masters/office-locations"),
        api.get("/masters/companies"),
      ]);
      setRolesCatalog(rls.data || []);
      setDepartments(dept.data.rows || []);
      setDesignations(des.data.rows || []);
      setEmploymentStatuses(es.data.rows || []);
      setOfficeLocations(ol.data.rows || []);
      setCompanies(co.data.rows || []);
    } catch { /* silent — user without permission still sees basic view */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadMasters(); }, [loadMasters]);
  useEffect(() => { setPage(1); }, [search, roleFilter, statusFilter, teamFilter]);

  // UAT Batch 1 — refresh eligible reporting managers whenever the primary
  // role selector changes in the Create modal.
  useEffect(() => {
    if (!createOpen || !createForm.role) { setCreateValidManagers([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/directory/valid-reporting-managers?role=${createForm.role}`);
        if (!cancelled) setCreateValidManagers(r.data.rows || []);
      } catch { if (!cancelled) setCreateValidManagers([]); }
    })();
    return () => { cancelled = true; };
  }, [createOpen, createForm.role]);
  // Same for Edit modal — driven by editForm.role.
  useEffect(() => {
    if (!editing || !editForm.role) { setEditValidManagers([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/directory/valid-reporting-managers?role=${editForm.role}`);
        if (!cancelled) setEditValidManagers(r.data.rows || []);
      } catch { if (!cancelled) setEditValidManagers([]); }
    })();
    return () => { cancelled = true; };
  }, [editing, editForm.role]);

  const loadTransfers = useCallback(() => {
    api.get("/employee-transfers").then((r) => setTransfers(r.data)).catch(() => {});
  }, []);

  const teams = useMemo(() => Array.from(new Set(items.map((e) => e.team).filter(Boolean))).sort(), [items]);
  const managerOptions = useMemo(() =>
    items.filter((e) => ["admin", "team_lead", "sdl"].includes(e.role) && !e.deleted_at), [items]);
  const employeesById = useMemo(() => {
    const m = new Map();
    items.forEach((e) => m.set(e.id, e));
    return m;
  }, [items]);
  const managerName = useCallback((id) => employeesById.get(id)?.name || "—", [employeesById]);
  const pages = Math.max(1, Math.ceil(total / pageSize));

  const uploadPicture = async (empId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/employees/${empId}/profile-picture`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Profile picture uploaded");
      load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  // -------- create -------- //
  const submitCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...createForm,
        reporting_manager_id: createForm.reporting_manager_id || null,
        roles: createForm.roles.length ? createForm.roles : null,
        date_of_joining: createForm.date_of_joining || null,
        managed_teams: createForm.role === "sdl" ? (createForm.managed_teams || []) : null,
        managed_groups: createForm.role === "team_lead" ? (createForm.managed_groups || []) : null,
        max_capacity: Number(createForm.max_capacity) || 30,
      };
      await api.post("/employees/full", payload);
      toast.success("Employee created");
      setCreateOpen(false); setCreateForm(EMPTY_CREATE); load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  // -------- edit -------- //
  const openEdit = (emp) => {
    setEditing(emp);
    setEditForm({
      name: emp.name || "",
      first_name: emp.first_name || "",
      last_name: emp.last_name || "",
      mobile: emp.mobile || "",
      date_of_joining: emp.date_of_joining || "",
      employment_status: emp.employment_status || (emp.active ? "Active" : "Inactive"),
      department: emp.department || "",
      designation: emp.designation || "",
      office_location: emp.office_location || "",
      company: emp.company || "",
      role: emp.role || "sde",
      // Store SECONDARY roles only (primary role is separate) so removing a chip
      // has clean semantics — no stale primary sneaking back into the array.
      roles: (emp.roles || []).filter((r) => r !== emp.role),
      reporting_manager_id: emp.reporting_manager_id || "",
      managed_teams: emp.managed_teams || [],
      managed_groups: emp.managed_groups || [],
      team: emp.team || "",
      max_capacity: emp.max_capacity ?? 30,
      version: emp.version || 1,
    });
  };
  const submitEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...editForm,
        reporting_manager_id: editForm.reporting_manager_id || null,
        max_capacity: Number(editForm.max_capacity) || 30,
        // ALWAYS send roles as explicit array (even []) so the backend clears
        // removed additional roles instead of falling into the "roles is None"
        // legacy branch that re-adds old_roles + primary.
        roles: Array.isArray(editForm.roles) ? editForm.roles : [],
      };
      await api.patch(`/employees/${editing.id}/master`, payload);
      toast.success("Employee updated");
      setEditing(null); load();
      // UAT — if the currently-logged-in admin edited their own record, refresh
      // their session so sidebar/menus update without logout.
      if (user?.id === editing.id) {
        try { await refreshMe?.(); } catch { /* silent */ }
      }
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  // -------- reset password -------- //
  const submitReset = async (e) => {
    e.preventDefault();
    if ((newPassword || "").length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.post(`/employees/${resetting.id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset for ${resetting.name}`);
      setResetting(null); setNewPassword("");
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  // -------- transfer -------- //
  const submitTransfer = async (e) => {
    e.preventDefault();
    if (!transferTo || transferTo === transferring.id) { toast.error("Pick a different destination employee"); return; }
    try {
      const r = await api.post("/employees/transfer", {
        from_employee_id: transferring.id, to_employee_id: transferTo,
        reason: transferReason || null,
      });
      const c = r.data?.counts || {};
      toast.success(`Transferred: ${c.candidates || 0} cand, ${c.applications || 0} apps`);
      setTransferring(null); setTransferTo(""); setTransferReason(""); load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const setStatus = async (emp, status) => {
    try {
      await api.patch(`/employees/${emp.id}/master`, { employment_status: status, version: emp.version || 1 });
      toast.success(`${emp.name} → ${status}`); load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const softDelete = async (emp) => {
    if (!confirm(`Deactivate ${emp.name}? Any assigned candidates must be transferred first.`)) return;
    try {
      await api.delete(`/employees/${emp.id}/soft`);
      toast.success(`${emp.name} deactivated`); load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="employees-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400">Administration</div>
          <h2 className="text-2xl font-semibold" data-testid="employee-management-title">Employee Management</h2>
          <p className="text-sm text-slate-500">
            Full employee master — departments, designations, roles, reporting hierarchy, capacity, profile pictures. All fields are backend-authoritative.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setHistoryOpen(true); loadTransfers(); }}
                  data-testid="view-transfer-history-button"
                  className="text-sm px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50 flex items-center gap-2">
            <ClockCounterClockwise size={16} /> Transfer history
          </button>
          {isAdmin && (
            <button onClick={() => setCreateOpen(true)} data-testid="add-employee-button"
                    className="bg-sky-600 hover:bg-sky-700 text-white text-sm px-4 py-2 rounded-md flex items-center gap-2">
              <UserPlus size={16} /> Add Employee
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="emp-search" value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search by name, email, EMP-#…"
                 className="w-full border border-slate-200 rounded-md pl-8 pr-3 py-2 text-sm" />
        </div>
        <select data-testid="emp-role-filter" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
                className="border border-slate-200 rounded-md px-3 py-2 text-sm">
          <option value="">All roles</option>
          {rolesCatalog.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
        </select>
        <select data-testid="emp-team-filter" value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)}
                className="border border-slate-200 rounded-md px-3 py-2 text-sm">
          <option value="">All teams</option>
          {teams.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select data-testid="emp-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-slate-200 rounded-md px-3 py-2 text-sm">
          <option value="">All statuses</option>
          {employmentStatuses.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
        </select>
        <div className="text-xs text-slate-500 ml-2" data-testid="emp-count">
          {items.length} on page · {total} total
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-widest">
            <tr>
              <th className="text-left px-4 py-3">Employee</th>
              <th className="text-left px-4 py-3">Role(s)</th>
              <th className="text-left px-4 py-3">Team / Dept</th>
              <th className="text-left px-4 py-3">Reporting to</th>
              <th className="text-left px-4 py-3">Capacity</th>
              <th className="text-left px-4 py-3">Status</th>
              {isAdmin && <th className="text-right px-4 py-3">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={isAdmin ? 7 : 6} className="text-center text-sm text-slate-500 py-6">No employees match.</td></tr>
            )}
            {items.map((e) => {
              const secondaryRoles = (e.roles || []).filter((r) => r !== e.role);
              return (
              <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`emp-row-${e.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-100 grid place-items-center overflow-hidden flex-shrink-0">
                      {e.profile_picture ? (
                        <img src={e.profile_picture} alt="" className="w-full h-full object-cover" />
                      ) : <UserIcon size={16} className="text-slate-400" />}
                    </div>
                    <div>
                      <b>{e.name}</b>
                      <div className="text-xs text-slate-500">{e.email}</div>
                      <div className="text-xs text-slate-400">
                        {e.employee_code || ""}{e.designation ? ` · ${e.designation}` : ""}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600">
                  <div><RoleName role={e.role} testid={`emp-role-${e.id}`} /></div>
                  {secondaryRoles.length > 0 && (
                    <div className="text-xs text-slate-400">+ {secondaryRoles.map(roleName).join(", ")}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-600">
                  <div>{e.team || "—"}</div>
                  {e.department && <div className="text-xs text-slate-400">{e.department}</div>}
                </td>
                <td className="px-4 py-3 text-slate-600" data-testid={`emp-manager-${e.id}`}>
                  {e.reporting_manager_name || managerName(e.reporting_manager_id)}
                </td>
                <td className="px-4 py-3 text-slate-600 tabular-nums">
                  <div>{e.current_load ?? 0}/{e.max_capacity ?? 30}</div>
                  <div className="text-xs text-emerald-700">{e.available_capacity ?? 0} avail</div>
                </td>
                <td className="px-4 py-3"><StatusBadge status={e.employment_status || (e.active ? "Active" : "Inactive")} /></td>
                {isAdmin && (
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1 flex-wrap">
                      <button onClick={() => openEdit(e)} data-testid={`edit-emp-${e.id}`}
                              className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-100 flex items-center gap-1">
                        <PencilSimple size={12} /> Edit
                      </button>
                      <label className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-100 flex items-center gap-1 cursor-pointer"
                             data-testid={`upload-pic-${e.id}`}>
                        <UploadSimple size={12} /> Photo
                        <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                               onChange={(ev) => ev.target.files?.[0] && uploadPicture(e.id, ev.target.files[0])} />
                      </label>
                      <button onClick={() => setResetting(e)} data-testid={`reset-emp-${e.id}`}
                              className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-100 flex items-center gap-1">
                        <Key size={12} /> Reset
                      </button>
                      <button onClick={() => setTransferring(e)} data-testid={`transfer-emp-${e.id}`}
                              className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-100 flex items-center gap-1">
                        <ArrowsLeftRight size={12} /> Transfer
                      </button>
                      <button onClick={() => setStatus(e, e.employment_status === "Active" ? "Inactive" : "Active")}
                              data-testid={`toggle-emp-${e.id}`}
                              className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-100 flex items-center gap-1">
                        <Power size={12} /> {e.employment_status === "Active" ? "Deactivate" : "Activate"}
                      </button>
                    </div>
                  </td>
                )}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > pageSize && (
        <div className="flex items-center justify-between text-sm">
          <div className="text-slate-500">
            Page {page} of {pages} · {total} total
          </div>
          <div className="flex gap-2">
            <button disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                    data-testid="emp-page-prev"
                    className="px-3 py-1.5 border border-slate-200 rounded-md disabled:opacity-40">Prev</button>
            <button disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))}
                    data-testid="emp-page-next"
                    className="px-3 py-1.5 border border-slate-200 rounded-md disabled:opacity-40">Next</button>
          </div>
        </div>
      )}

      {/* ===== Create modal ===== */}
      {createOpen && (
        <Modal onClose={() => setCreateOpen(false)} title="Add Employee" testid="create-employee-modal" wide>
          <form onSubmit={submitCreate} className="grid grid-cols-2 gap-3">
            <Field label="Full name *">
              <input required minLength={3} data-testid="create-emp-name" className="input"
                     value={createForm.name} onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))} />
            </Field>
            <Field label="Email *">
              <input required type="email" data-testid="create-emp-email" className="input"
                     value={createForm.email} onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))} />
            </Field>
            <Field label="First name">
              <input data-testid="create-emp-first-name" className="input"
                     value={createForm.first_name} onChange={(e) => setCreateForm((f) => ({ ...f, first_name: e.target.value }))} />
            </Field>
            <Field label="Last name">
              <input data-testid="create-emp-last-name" className="input"
                     value={createForm.last_name} onChange={(e) => setCreateForm((f) => ({ ...f, last_name: e.target.value }))} />
            </Field>
            <Field label="Mobile">
              <input data-testid="create-emp-mobile" className="input" inputMode="tel"
                     value={createForm.mobile} onChange={(e) => setCreateForm((f) => ({ ...f, mobile: e.target.value }))} />
            </Field>
            <Field label="Temporary password *">
              <input required type="text" minLength={6} data-testid="create-emp-password" className="input"
                     value={createForm.password} onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))} />
            </Field>
            <Field label="Date of joining">
              <input type="date" max={new Date().toISOString().slice(0, 10)}
                     data-testid="create-emp-joining" className="input"
                     value={createForm.date_of_joining} onChange={(e) => setCreateForm((f) => ({ ...f, date_of_joining: e.target.value }))} />
            </Field>
            <Field label="Employment status">
              <select data-testid="create-emp-employment-status" className="input"
                      value={createForm.employment_status} onChange={(e) => setCreateForm((f) => ({ ...f, employment_status: e.target.value }))}>
                {employmentStatuses.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="Primary role *">
              <select data-testid="create-emp-role" className="input"
                      value={createForm.role} onChange={(e) => setCreateForm((f) => ({ ...f, role: e.target.value }))}>
                {rolesCatalog.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
              </select>
            </Field>
            <Field label="Additional roles">
              <select multiple data-testid="create-emp-roles" className="input h-24"
                      value={createForm.roles}
                      onChange={(e) => setCreateForm((f) => ({ ...f, roles: [...e.target.selectedOptions].map((o) => o.value) }))}>
                {rolesCatalog.filter(r => r.key !== createForm.role).map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
              </select>
            </Field>
            <Field label="Department">
              <select data-testid="create-emp-department" className="input"
                      value={createForm.department} onChange={(e) => setCreateForm((f) => ({ ...f, department: e.target.value }))}>
                <option value="">— none —</option>
                {departments.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
            </Field>
            <Field label="Designation">
              <select data-testid="create-emp-designation" className="input"
                      value={createForm.designation} onChange={(e) => setCreateForm((f) => ({ ...f, designation: e.target.value }))}>
                <option value="">— none —</option>
                {designations.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
            </Field>
            <Field label="Office location">
              <select data-testid="create-emp-office" className="input"
                      value={createForm.office_location} onChange={(e) => setCreateForm((f) => ({ ...f, office_location: e.target.value }))}>
                <option value="">— none —</option>
                {officeLocations.map((o) => <option key={o.id} value={o.name}>{o.name}</option>)}
              </select>
            </Field>
            <Field label="Company">
              <select data-testid="create-emp-company" className="input"
                      value={createForm.company} onChange={(e) => setCreateForm((f) => ({ ...f, company: e.target.value }))}>
                <option value="">— none —</option>
                {companies.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Reporting manager">
              <select data-testid="create-emp-manager" className="input"
                      value={createForm.reporting_manager_id}
                      onChange={(e) => setCreateForm((f) => ({ ...f, reporting_manager_id: e.target.value }))}>
                <option value="">— none —</option>
                {createValidManagers.map((m) => <option key={m.id} value={m.id}>{`${m.name} (${roleName(m.role)})`}</option>)}
              </select>
              {createValidManagers.length === 0 && createForm.role && !["admin", "super_admin", "hr"].includes(createForm.role) && (
                <span className="text-[11px] text-amber-700">No eligible manager found — create one first</span>
              )}
            </Field>
            <Field label="Team">
              <input data-testid="create-emp-team" className="input" list="team-options"
                     value={createForm.team} onChange={(e) => setCreateForm((f) => ({ ...f, team: e.target.value }))} />
              <datalist id="team-options">{teams.map((t) => <option key={t} value={t} />)}</datalist>
            </Field>
            {createForm.role === "sdl" && (
              <Field label="Managed Teams (Group Lead)">
                <TeamAccessPicker
                  value={createForm.managed_teams || []}
                  onChange={(v) => setCreateForm((f) => ({ ...f, managed_teams: v }))}
                  teams={teams}
                  testid="create-emp-managed-teams"
                />
              </Field>
            )}
            {createForm.role === "team_lead" && (
              <Field label="Managed Groups (Manager)">
                <GroupAccessPicker
                  value={createForm.managed_groups || []}
                  onChange={(v) => setCreateForm((f) => ({ ...f, managed_groups: v }))}
                  testid="create-emp-managed-groups"
                />
              </Field>
            )}
            <Field label="Max capacity">
              <input type="number" min={0} max={200} data-testid="create-emp-capacity" className="input"
                     value={createForm.max_capacity} onChange={(e) => setCreateForm((f) => ({ ...f, max_capacity: e.target.value }))} />
            </Field>
            <div className="col-span-2">
              <AccessPreviewPanel
                role={createForm.role}
                team={createForm.team}
                managedTeams={createForm.managed_teams || []}
                managedGroups={createForm.managed_groups || []}
                testid="create-emp-access-preview"
              />
            </div>
            <div className="col-span-2 flex justify-end gap-2 mt-2">
              <button type="button" onClick={() => setCreateOpen(false)} className="px-4 py-2 text-sm rounded-md border border-slate-200">Cancel</button>
              <button type="submit" data-testid="create-emp-submit"
                      className="px-4 py-2 text-sm rounded-md bg-sky-600 hover:bg-sky-700 text-white">Create employee</button>
            </div>
          </form>
        </Modal>
      )}

      {/* ===== Edit modal ===== */}
      {editing && (
        <Modal onClose={() => setEditing(null)} title={`Edit — ${editing.name} (${editing.employee_code || ""})`} testid="edit-employee-modal" wide>
          <form onSubmit={submitEdit} className="grid grid-cols-2 gap-3">
            <Field label="Full name">
              <input required minLength={3} data-testid="edit-emp-name" className="input"
                     value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} />
            </Field>
            <Field label="Mobile">
              <input data-testid="edit-emp-mobile" className="input"
                     value={editForm.mobile} onChange={(e) => setEditForm((f) => ({ ...f, mobile: e.target.value }))} />
            </Field>
            <Field label="First name">
              <input data-testid="edit-emp-first-name" className="input"
                     value={editForm.first_name} onChange={(e) => setEditForm((f) => ({ ...f, first_name: e.target.value }))} />
            </Field>
            <Field label="Last name">
              <input data-testid="edit-emp-last-name" className="input"
                     value={editForm.last_name} onChange={(e) => setEditForm((f) => ({ ...f, last_name: e.target.value }))} />
            </Field>
            <Field label="Date of joining">
              <input type="date" max={new Date().toISOString().slice(0, 10)}
                     data-testid="edit-emp-joining" className="input"
                     value={editForm.date_of_joining} onChange={(e) => setEditForm((f) => ({ ...f, date_of_joining: e.target.value }))} />
            </Field>
            <Field label="Employment status">
              <select data-testid="edit-emp-employment-status" className="input"
                      value={editForm.employment_status} onChange={(e) => setEditForm((f) => ({ ...f, employment_status: e.target.value }))}>
                {employmentStatuses.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="Primary role">
              <select data-testid="edit-emp-role" className="input"
                      value={editForm.role} onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value }))}>
                {rolesCatalog.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
              </select>
            </Field>
            <Field label="Additional roles">
              <div className="border border-slate-200 rounded-md p-2 space-y-2" data-testid="edit-emp-roles-container">
                <div className="flex flex-wrap gap-1">
                  {(editForm.roles || []).filter((r) => r !== editForm.role).length === 0 && (
                    <span className="text-xs text-slate-400 px-1">No additional roles</span>
                  )}
                  {(editForm.roles || []).filter((r) => r !== editForm.role).map((rk) => {
                    const meta = rolesCatalog.find((r) => r.key === rk);
                    return (
                      <span key={rk} data-testid={`edit-emp-role-chip-${rk}`}
                            className="inline-flex items-center gap-1 text-xs bg-sky-50 text-sky-700 border border-sky-200 rounded-full px-2 py-0.5">
                        {meta?.name || rk}
                        <button type="button" data-testid={`edit-emp-role-remove-${rk}`}
                                onClick={() => setEditForm((f) => ({ ...f, roles: (f.roles || []).filter((x) => x !== rk) }))}
                                className="text-sky-600 hover:text-red-600" aria-label="Remove role">×</button>
                      </span>
                    );
                  })}
                </div>
                <select data-testid="edit-emp-role-add" className="input text-sm"
                        value=""
                        onChange={(e) => {
                          const rk = e.target.value;
                          if (!rk) return;
                          setEditForm((f) => ({ ...f, roles: [...new Set([...(f.roles || []), rk])].filter((x) => x !== f.role) }));
                        }}>
                  <option value="">＋ Add role…</option>
                  {rolesCatalog
                    .filter((r) => r.key !== editForm.role && !(editForm.roles || []).includes(r.key))
                    .map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                </select>
              </div>
            </Field>

            {/* Phase 9 — role-scoped access configuration */}
            {editForm.role === "sdl" && (
              <Field label="Managed Teams (Group Lead)">
                <TeamAccessPicker
                  value={editForm.managed_teams || []}
                  onChange={(v) => setEditForm((f) => ({ ...f, managed_teams: v }))}
                  teams={teams}
                  testid="edit-emp-managed-teams"
                />
              </Field>
            )}
            {editForm.role === "team_lead" && (
              <Field label="Managed Groups (Manager)">
                <GroupAccessPicker
                  value={editForm.managed_groups || []}
                  onChange={(v) => setEditForm((f) => ({ ...f, managed_groups: v }))}
                  testid="edit-emp-managed-groups"
                />
              </Field>
            )}
            <Field label="Department">
              <select data-testid="edit-emp-department" className="input"
                      value={editForm.department} onChange={(e) => setEditForm((f) => ({ ...f, department: e.target.value }))}>
                <option value="">— none —</option>
                {departments.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
            </Field>
            <Field label="Designation">
              <select data-testid="edit-emp-designation" className="input"
                      value={editForm.designation} onChange={(e) => setEditForm((f) => ({ ...f, designation: e.target.value }))}>
                <option value="">— none —</option>
                {designations.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
            </Field>
            <Field label="Office location">
              <select data-testid="edit-emp-office" className="input"
                      value={editForm.office_location} onChange={(e) => setEditForm((f) => ({ ...f, office_location: e.target.value }))}>
                <option value="">— none —</option>
                {officeLocations.map((o) => <option key={o.id} value={o.name}>{o.name}</option>)}
              </select>
            </Field>
            <Field label="Company">
              <select data-testid="edit-emp-company" className="input"
                      value={editForm.company} onChange={(e) => setEditForm((f) => ({ ...f, company: e.target.value }))}>
                <option value="">— none —</option>
                {companies.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Reporting manager">
              <select data-testid="edit-emp-manager" className="input"
                      value={editForm.reporting_manager_id}
                      onChange={(e) => setEditForm((f) => ({ ...f, reporting_manager_id: e.target.value }))}>
                <option value="">— none —</option>
                {editValidManagers.filter((m) => m.id !== editing.id).map((m) => (
                  <option key={m.id} value={m.id}>{`${m.name} (${roleName(m.role)})`}</option>
                ))}
              </select>
              {editValidManagers.length === 0 && editForm.role && !["admin", "super_admin", "hr"].includes(editForm.role) && (
                <span className="text-[11px] text-amber-700">No eligible manager found for this role</span>
              )}
            </Field>
            <Field label="Team">
              <input data-testid="edit-emp-team" className="input" list="team-options"
                     value={editForm.team} onChange={(e) => setEditForm((f) => ({ ...f, team: e.target.value }))} />
            </Field>
            <Field label="Max capacity">
              <input type="number" min={0} max={200} data-testid="edit-emp-capacity" className="input"
                     value={editForm.max_capacity}
                     onChange={(e) => setEditForm((f) => ({ ...f, max_capacity: e.target.value }))} />
            </Field>
            <div className="col-span-2">
              <AccessPreviewPanel
                role={editForm.role}
                team={editForm.team}
                managedTeams={editForm.managed_teams || []}
                managedGroups={editForm.managed_groups || []}
                testid="edit-emp-access-preview"
              />
            </div>
            <div className="col-span-2 flex justify-between gap-2 mt-2">
              <button type="button" onClick={() => softDelete(editing)}
                      data-testid="edit-emp-soft-delete"
                      className="px-3 py-2 text-sm rounded-md text-red-600 border border-red-200 hover:bg-red-50">
                Deactivate (soft delete)
              </button>
              <div className="flex gap-2">
                <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 text-sm rounded-md border border-slate-200">Cancel</button>
                <button type="submit" data-testid="edit-emp-submit"
                        className="px-4 py-2 text-sm rounded-md bg-sky-600 hover:bg-sky-700 text-white">Save changes</button>
              </div>
            </div>
          </form>
        </Modal>
      )}

      {/* ===== Reset password modal ===== */}
      {resetting && (
        <Modal onClose={() => { setResetting(null); setNewPassword(""); }} title={`Reset password — ${resetting.name}`} testid="reset-password-modal">
          <form onSubmit={submitReset} className="space-y-3">
            <p className="text-sm text-slate-500">Set a new temporary password. The employee should change it after login.</p>
            <input type="text" required minLength={6} placeholder="New password (min 6 chars)"
                   data-testid="reset-emp-new-password"
                   value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                   className="input w-full" />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setResetting(null); setNewPassword(""); }} className="px-4 py-2 text-sm rounded-md border border-slate-200">Cancel</button>
              <button type="submit" data-testid="reset-emp-submit"
                      className="px-4 py-2 text-sm rounded-md bg-sky-600 hover:bg-sky-700 text-white">Reset password</button>
            </div>
          </form>
        </Modal>
      )}

      {/* ===== Transfer modal ===== */}
      {transferring && (
        <Modal onClose={() => { setTransferring(null); setTransferTo(""); setTransferReason(""); }}
               title={`Transfer workload — ${transferring.name}`} testid="transfer-employee-modal">
          <form onSubmit={submitTransfer} className="space-y-3">
            <p className="text-sm text-slate-500">
              Reassigns all candidates, applications, interviews, offers and cold emails currently owned by
              <b> {transferring.name}</b> to the selected destination employee.
            </p>
            <Field label="Transfer to">
              <select required data-testid="transfer-to-select" className="input w-full"
                      value={transferTo} onChange={(e) => setTransferTo(e.target.value)}>
                <option value="">— Select employee —</option>
                {items.filter((e) => e.id !== transferring.id).map((e) => (
                  <option key={e.id} value={e.id}>{`${e.name} — ${roleName(e.role)} ${e.team ? `(${e.team})` : ""}`}</option>
                ))}
              </select>
            </Field>
            <Field label="Reason (optional)">
              <textarea data-testid="transfer-reason" rows={3} className="input w-full"
                        value={transferReason} onChange={(e) => setTransferReason(e.target.value)} />
            </Field>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setTransferring(null); setTransferTo(""); setTransferReason(""); }} className="px-4 py-2 text-sm rounded-md border border-slate-200">Cancel</button>
              <button type="submit" data-testid="transfer-submit"
                      className="px-4 py-2 text-sm rounded-md bg-sky-600 hover:bg-sky-700 text-white">Transfer</button>
            </div>
          </form>
        </Modal>
      )}

      {/* ===== Transfer history drawer ===== */}
      {historyOpen && (
        <Modal onClose={() => setHistoryOpen(false)} title="Employee transfer history" testid="transfer-history-modal" wide>
          <div className="max-h-[60vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-widest">
                <tr>
                  <th className="text-left px-3 py-2">When</th>
                  <th className="text-left px-3 py-2">From</th>
                  <th className="text-left px-3 py-2">To</th>
                  <th className="text-left px-3 py-2">Reason</th>
                  <th className="text-right px-3 py-2">Reassigned</th>
                  <th className="text-left px-3 py-2">By</th>
                </tr>
              </thead>
              <tbody>
                {transfers.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-slate-500 py-6">No transfers yet.</td></tr>
                )}
                {transfers.map((t) => (
                  <tr key={t.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-slate-500">{(t.performed_at || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="px-3 py-2">{t.from_name}</td>
                    <td className="px-3 py-2">{t.to_name}</td>
                    <td className="px-3 py-2 text-slate-500">{t.reason || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {t.counts?.candidates || 0} / {t.counts?.applications || 0} / {t.counts?.interviews || 0} / {t.counts?.offers || 0} / {t.counts?.cold_emails || 0}
                    </td>
                    <td className="px-3 py-2 text-slate-500">{t.performed_by_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-xs text-slate-400 mt-2">Counts: Candidates / Applications / Interviews / Offers / Cold emails</div>
          </div>
        </Modal>
      )}

      <style>{`.input{border:1px solid #e2e8f0;border-radius:6px;padding:8px 12px;font-size:14px;background:#fff;}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="text-xs text-slate-500 space-y-1 flex flex-col">
      <span>{label}</span>
      {children}
    </label>
  );
}

function Modal({ children, onClose, title, testid, wide }) {
  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm grid place-items-center p-4 z-50" data-testid={testid}>
      <div className={`bg-white border border-slate-200 rounded-xl w-full ${wide ? "max-w-4xl" : "max-w-lg"} max-h-[90vh] flex flex-col`}>
        <div className="flex items-center justify-between p-6 pb-3 border-b border-slate-100 flex-shrink-0">
          <h3 className="font-semibold">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-sm">Close</button>
        </div>
        <div className="p-6 overflow-y-auto flex-1">
          {children}
        </div>
      </div>
    </div>
  );
}

/* Phase 9 — Access-configuration pickers ------------------------------ */
function TeamAccessPicker({ value, onChange, teams, testid }) {
  const isAll = (value || []).includes("*");
  return (
    <div className="border border-slate-200 rounded-md p-2 space-y-2" data-testid={testid}>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={isAll}
               data-testid={`${testid}-all`}
               onChange={(e) => onChange(e.target.checked ? ["*"] : [])} />
        All teams
      </label>
      {!isAll && (
        <div className="flex flex-wrap gap-1">
          {(value || []).map((t) => (
            <span key={t} className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">
              {t}
              <button type="button" onClick={() => onChange((value || []).filter((x) => x !== t))}
                      className="text-emerald-600 hover:text-red-600">×</button>
            </span>
          ))}
          <select className="input text-sm" value=""
                  onChange={(e) => { if (e.target.value) onChange([...new Set([...(value || []), e.target.value])]); }}>
            <option value="">＋ Add team…</option>
            {(teams || []).filter((t) => !(value || []).includes(t)).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}

function GroupAccessPicker({ value, onChange, testid }) {
  const [groups, setGroups] = useState([]);
  useEffect(() => { (async () => { try { const r = await api.get("/masters/groups"); setGroups((r.data.rows || []).map((g) => g.name)); } catch {} })(); }, []);
  const isAll = (value || []).includes("*");
  return (
    <div className="border border-slate-200 rounded-md p-2 space-y-2" data-testid={testid}>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={isAll}
               data-testid={`${testid}-all`}
               onChange={(e) => onChange(e.target.checked ? ["*"] : [])} />
        All groups
      </label>
      {!isAll && (
        <div className="flex flex-wrap gap-1">
          {(value || []).map((g) => (
            <span key={g} className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full px-2 py-0.5">
              {g}
              <button type="button" onClick={() => onChange((value || []).filter((x) => x !== g))}
                      className="text-indigo-600 hover:text-red-600">×</button>
            </span>
          ))}
          <select className="input text-sm" value=""
                  onChange={(e) => { if (e.target.value) onChange([...new Set([...(value || []), e.target.value])]); }}>
            <option value="">＋ Add group…</option>
            {groups.filter((g) => !(value || []).includes(g)).map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          {groups.length === 0 && (
            <div className="text-xs text-slate-500">No groups yet — create via Masters → Groups.</div>
          )}
        </div>
      )}
    </div>
  );
}

/* Phase 9 Slice C — live Access Preview panel used by Create + Edit modals */
function AccessPreviewPanel({ role, team, managedTeams, managedGroups, testid }) {
  const [state, setState] = useState({ loading: false, data: null, error: null });
  const managedTeamsKey = JSON.stringify(managedTeams || []);
  const managedGroupsKey = JSON.stringify(managedGroups || []);
  useEffect(() => {
    if (!role) return;
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const params = new URLSearchParams({ role });
        if (team) params.set("team", team);
        const mt = JSON.parse(managedTeamsKey);
        const mg = JSON.parse(managedGroupsKey);
        if (mt.length) params.set("managed_teams", mt.join(","));
        if (mg.length) params.set("managed_groups", mg.join(","));
        const r = await api.get(`/masters/access-preview?${params.toString()}`, { signal: ctrl.signal });
        setState({ loading: false, data: r.data, error: null });
      } catch (ex) {
        if (ex.name === "CanceledError") return;
        setState({ loading: false, data: null, error: ex.response?.data?.detail || "Preview failed" });
      }
    }, 300); // debounce
    return () => { clearTimeout(timer); ctrl.abort(); };
  }, [role, team, managedTeamsKey, managedGroupsKey]);

  const d = state.data;
  return (
    <div className="mt-2 border border-sky-200 bg-sky-50/40 rounded-md p-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wide text-sky-800 font-semibold">Access Preview</div>
        {state.loading && <div className="text-xs text-slate-500">Calculating…</div>}
        {d && d.unrestricted && (
          <span className="text-xs bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-full px-2 py-0.5">Unrestricted (full org)</span>
        )}
      </div>
      {state.error && <div className="text-xs text-red-600">{state.error}</div>}
      {d && !state.error && (
        <>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-center">
            <PreviewStat label="Candidates" value={d.candidates_visible} testid={`${testid}-candidates`} />
            <PreviewStat label="Teams" value={d.teams_accessible} testid={`${testid}-teams`} />
            <PreviewStat label="Groups" value={d.groups_accessible} testid={`${testid}-groups`} />
            <PreviewStat label="Executives" value={d.executives_visible} testid={`${testid}-execs`} />
            <PreviewStat label="Team Leaders" value={d.team_leaders_visible} testid={`${testid}-tls`} />
            <PreviewStat label="Group Leads" value={d.group_leads_visible} testid={`${testid}-gls`} />
          </div>
          {(d.sample_candidates || []).length > 0 && (
            <div className="mt-3">
              <div className="text-xs text-slate-500 mb-1">Sample candidates ({(d.sample_candidates || []).length}):</div>
              <div className="flex flex-wrap gap-1" data-testid={`${testid}-samples`}>
                {(d.sample_candidates || []).map((n, i) => (
                  <span key={`${n}-${i}`} className="text-xs bg-white border border-slate-200 rounded-full px-2 py-0.5">{n}</span>
                ))}
              </div>
            </div>
          )}
          {(d.sample_candidates || []).length === 0 && (
            <div className="mt-3 text-xs text-slate-500">No candidates visible yet for this configuration.</div>
          )}
        </>
      )}
      {!d && !state.loading && !state.error && (
        <div className="text-xs text-slate-500">Pick a role to see who this employee will be able to see.</div>
      )}
    </div>
  );
}

function PreviewStat({ label, value, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md py-1.5">
      <div className="text-lg font-semibold text-slate-900" data-testid={testid}>{value ?? 0}</div>
      <div className="text-[10px] uppercase text-slate-500 tracking-wide">{label}</div>
    </div>
  );
}
